import type { StatusBadgeTooltip } from '@civilgrid/ui/components';
import type { Agency, UtilityNote } from '~/common/types';

// PostHog release toggle for the Connectbase-coverage states on the Utilities tab.
export const CONNECTBASE_COVERAGE_FLAG = 'utilities-show-connectbase-coverage';

// Live Map tooltip for a Connectbase-covered carrier; customer-facing copy must not name the vendor.
export const LIVE_DATA_AVAILABLE_TOOLTIP: StatusBadgeTooltip = {
  title: 'Live Data Available',
  description: 'Fiber routes for this carrier are shown on the live map.',
};

// CG-1219: PostHog gate for rows unioned in from data requests whose agency left the project list.
export const REQUEST_ONLY_AGENCIES_FLAG = 'utilities-show-request-only-agencies';

// PostHog gate for the Utilities page redesign.
export const UTILITIES_PAGE_REDESIGN_FLAG = 'utilities-page-redesign';

// Opens the Exports menu with the reports highlighted; set by the Reports popover.
export const EXPORTS_MENU_SEARCH_PARAM = 'exports';
export const EXPORTS_MENU_SHOW_REPORTS = 'reports';
// Same, and focuses the first report; used when Show me was pressed from the keyboard.
export const EXPORTS_MENU_FOCUS_REPORTS = 'reports-focus';

export type RefreshCadence = 'Quarterly' | 'Monthly';

//@ts-expect-error
export interface UtilityAgency extends Agency {
  assetsInProjectArea: boolean;
  availableDigitally: boolean;
  hasConnectbaseRoute?: boolean;
  dataRequestable: boolean;
  asbuiltsAvailable: boolean;
  refreshCadence: RefreshCadence;
  isDigitized: boolean;
  utilityNotes: UtilityNote;
  applicableRestrictions?: Partial<Agency['restrictions']>;
  subRows?: UtilityAgency[];
  contacts: UtilityContact[];
  // CG-611: present only on synthesized sub-agency rows — the sub-geometry whose
  // data requests this row represents (null on the catch-all/leftover-area child).
  // Root/legacy/flat rows leave it undefined.
  agencySubGeometryId?: string | null;
  // CG-1219: true on a row that exists only because the agency backs a data request — it is no
  // longer in the project's agency list, so every projectAgencies-derived field is a default.
  isRequestOnly?: boolean;
  // CG-611: true on a summary parent row — an agency split across >1 record. The parent
  // shows only the agency name + expander; all its requests live on the Region children.
  isSubGeometryParent?: boolean;
  // True when one of the row's own requests has a file, or for a summary parent any Region's (set by withFileFlags).
  hasFiles?: boolean;
}

export interface DataRequestFormData {
  agency: UtilityAgency;
  mapsRequested: boolean;
  asbuiltsRequested: boolean;
}

type UtilityContactInfo = {
  name?: string;
  email?: string;
};

export interface UtilityContact {
  name?: string;
  contact_type?: string;
  contact?: string;
  email?: string;
  phone?: string;
  cell?: string;
  contacts?: UtilityContactInfo[];
  status?: string;
  isDefaultContact?: boolean;
}

export function hasNoAssetsPresent(a: UtilityAgency) {
  return a.isPublished && !a.assetsInProjectArea;
}

/**
 * Orders agency rows by name (case-insensitive, numbers in numeric order so Region 2 precedes
 * Region 10), then asset type, then id. Shared by the Utilities table and the Outreach Log so
 * rows that share a name land in the same order on screen and in every download.
 */
export function compareAgencyRows(a: UtilityAgency, b: UtilityAgency) {
  return (
    a.agencyName.localeCompare(b.agencyName, 'en', { numeric: true, sensitivity: 'base' }) ||
    a.type.localeCompare(b.type) ||
    a.id.localeCompare(b.id)
  );
}

/**
 * Whether a Utilities row counts as covered by a Connectbase route: the coverage flag is on and the API
 * matched a stored route for this project. `hasConnectbaseRoute` is absent on rows without a match, so
 * this is not a plain boolean read.
 */
export function isConnectbaseCovered(a: Pick<UtilityAgency, 'hasConnectbaseRoute'>, enabled: boolean): boolean {
  return enabled && a.hasConnectbaseRoute === true;
}
